import React, { createContext, useContext } from "react";
import { useDismissibleMenu } from "../hooks/useDismissibleMenu";

const RowActionsMenuContext = createContext({ close: () => {} });

const MENU_ITEM_CLASS =
  "block w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-surface-raised)]";

const MENU_PANEL_CLASS =
  "absolute right-0 z-20 mt-1 min-w-[12rem] rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-md";

const TRIGGER_CLASS =
  "rounded px-2 py-1 text-sm font-medium text-[var(--color-muted)] hover:bg-[var(--color-surface-raised)] hover:text-[var(--color-foreground)]";

/**
 * Table row overflow menu: trigger, panel, and dismiss-on-outside/Escape behavior.
 */
export default function RowActionsMenu({
  className = "",
  children,
  ariaLabel = "Row actions",
}) {
  const { open, wrapRef, toggle, close } = useDismissibleMenu();

  return (
    <RowActionsMenuContext.Provider value={{ close }}>
      <div
        className={`relative inline-block text-left ${className}`.trim()}
        ref={wrapRef}
      >
        <button
          type="button"
          className={TRIGGER_CLASS}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
        >
          ...
        </button>
        {open ? (
          <div className={MENU_PANEL_CLASS} role="menu" aria-label={ariaLabel}>
            {children}
          </div>
        ) : null}
      </div>
    </RowActionsMenuContext.Provider>
  );
}

/**
 * One action inside a RowActionsMenu. Closes the menu before onSelect runs.
 */
export function RowActionsMenuItem({ children, onSelect }) {
  const { close } = useContext(RowActionsMenuContext);

  return (
    <button
      type="button"
      role="menuitem"
      className={MENU_ITEM_CLASS}
      onClick={() => {
        close();
        onSelect?.();
      }}
    >
      {children}
    </button>
  );
}

/**
 * Navigation action inside a RowActionsMenu. Closes the menu before navigation.
 */
export function RowActionsMenuLink({ href, children }) {
  const { close } = useContext(RowActionsMenuContext);

  return (
    <a
      role="menuitem"
      href={href}
      className={`${MENU_ITEM_CLASS} no-underline text-inherit`}
      onClick={close}
    >
      {children}
    </a>
  );
}
